import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "my-4" }
const _hoisted_2 = { style: { color: '#D4572A' } }
const _hoisted_3 = { class: "register-call-center-modal" }
const _hoisted_4 = { class: "register-call-center-modal-header" }
const _hoisted_5 = { class: "register-call-center-modal-body" }
const _hoisted_6 = {
  key: 0,
  class: "register-call-center-info"
}
const _hoisted_7 = { class: "register-call-center-info-label" }
const _hoisted_8 = {
  key: 1,
  class: "register-call-center-info"
}
const _hoisted_9 = { class: "register-call-center-info-label" }
const _hoisted_10 = { class: "register-call-center-phone-desktop" }
const _hoisted_11 = ["href"]
const _hoisted_12 = ["href"]

import {
    reactive,
    computed,
    onMounted,
    onBeforeUnmount,
    watch,
    ref,
  } from 'vue'
  import InformationsForm from '@/components/LoginRegister/subforms/InformationsForm/v1/InformationsForm.vue'
  import ContactsForm from '@/components/LoginRegister/subforms/ContactsForm/v1/ContactsForm.vue'
  import KeepInTouchOptIn from '@/components/LoginRegister/KeepInTouchOptIn/KeepInTouchOptIn.vue'
  import Button from '@/components/LoginRegister/common/Button/v1/Button.vue'
  import { isCdaOptInEnabledForAll } from '@/logic/contact/isCdaOptInEnabled'
  import Banner from '@/components/LoginRegister/common/Banner/v1/Banner.vue'
  import ModalBase from '@/components/UI/ModalBase.vue'
  import { onKeyStroke } from '@vueuse/core'
  import useIsEmailValid from '@/composables/useIsEmailValid'
  import scrollIntoView from 'smooth-scroll-into-view-if-needed'

  import RegisterForm from '@/model/Form/RegisterForm'
  import registerForm from '@/state/registerForm'
  import useLoading from '@/composables/useLoading'
  import { useValidation } from '@/composables/useValidation'

  import register from '@/logic/contact/register'
  import resetRegisterForm from '@/logic/user-info/resetRegisterForm'
  import email from '@/state/email'
  import { isPhoneNumberValid, getInvalidPhoneMessage } from '@/components/LoginRegister/phoneNumberValidation'
  import {
    getHeaderContactInfo,
    HeaderContactInfo,
  } from '@/logic/headerFooter/contactInfo'

  import * as _ from 'lodash'

  import { useI18n } from 'vue-i18n'
  const minPasswordLength = 6

  interface Emits {
    (e: 'registrationSuccess'): void
    (e: 'emailChange', email: string): void
    (e: 'useOtherEmail'): void
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'RegisterForm',
  emits: ["registrationSuccess", "emailChange", "useOtherEmail"],
  setup(__props, { emit: __emit }) {

  const { t } = useI18n()

  const state = reactive({
    showErrors: false,
    isDisplayGeneralErrorMessage: null as string | null,
  })

  registerForm.value = new RegisterForm(email.value)

  const emit = __emit

  /** Register has no iresa id yet — only show when allowlist is `*` / `all`. */
  const showCdaOptIn = computed(() => isCdaOptInEnabledForAll())
  const btnDisabled = computed(() => {
    return state.showErrors && invalidFields.value.size > 0 ? true : false
  })
  const isEmailValid = useIsEmailValid(email)

  const validationRules = {
    first_name: (value: string) => value.trim().length > 0,
    last_name: (value: string) => value.trim().length > 0,
    email: () => useIsEmailValid(email).value,
    password1: (value: string) => value.length > minPasswordLength,
    password2: (value: string) => value.length > minPasswordLength,
    address: (value: string) => value.trim().length > 0,
    city: (value: string) => value.trim().length > 0,
    postal_code: (value: string) => value.trim().length > 0,
    country: (value: string) => value.trim().length > 0,
    phone_code: (value: string) => value.trim().length > 0,
    phone_number: (value: string) =>
      isPhoneNumberValid(value, registerForm.value?.phone_code || ''),
  }

  const { invalidFields } = useValidation<RegisterForm | null>(
    registerForm,
    validationRules
  )

  const submitClickCount = ref<number>(0)
  const isCallCenterMode = ref<boolean>(false)
  const pendingRetryAfterPhoneChange = ref<boolean>(false)
  const callCenterModalRef = ref<InstanceType<typeof ModalBase> | null>(null)
  const callCenterContactInfo = ref<HeaderContactInfo>({
    phone: '',
    openingHours: '',
    customerServiceLabel: '',
  })
  const callCenterPhoneHref = computed(
    () => `tel:${callCenterContactInfo.value.phone.replace(/\s/g, '')}`
  )

  const showCallCenterModal = async () => {
    callCenterContactInfo.value = await getHeaderContactInfo()
    isCallCenterMode.value = true
    callCenterModalRef.value?.open()
  }

  const handleButtonClick = async () => {
    if (!registerForm.value)
      throw new Error('registerForm.value cannot be null')

    state.isDisplayGeneralErrorMessage = null

    if (isCallCenterMode.value) {
      await showCallCenterModal()
      return
    }

    const isPhoneChangeRetry = pendingRetryAfterPhoneChange.value
    if (!isPhoneChangeRetry) {
      submitClickCount.value += 1
      if (submitClickCount.value > 3) {
        await showCallCenterModal()
        return
      }
    }

    if (invalidFields.value.size > 0) {
      state.showErrors = true
      const selector = `#${[...invalidFields.value][0]}`
      const invalidFieldElement = document.querySelector(
        selector
      ) as HTMLElement

      await scrollIntoView(invalidFieldElement, {
        ease: t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
        duration: 750,
        block: 'center',
        inline: 'nearest',
      })
      return
    }
    state.showErrors = false

    if (isPhoneChangeRetry) {
      pendingRetryAfterPhoneChange.value = false
    }

    const { startLoadingThis, stopLoadingThis } = useLoading()
    startLoadingThis()
    try {
      await register(registerForm.value)
      state.isDisplayGeneralErrorMessage = null
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : ''
      if (message.includes('phone_number')) {
        state.isDisplayGeneralErrorMessage = getInvalidPhoneMessage(
          t,
          registerForm.value.phone_code
        )
      } else {
        state.isDisplayGeneralErrorMessage = t(
          'loginRegister.error_wrong_email'
        )
      }
      if (isPhoneChangeRetry) {
        isCallCenterMode.value = true
      }
    }
    stopLoadingThis()
  }

  const handleUpdateForm = (newForm: RegisterForm) => {
    const didPhoneChange =
      newForm.phone_number !== registerForm.value?.phone_number ||
      newForm.phone_code !== registerForm.value?.phone_code
    const formClone = _.cloneDeep(newForm)
    registerForm.value = formClone
    if (didPhoneChange && isCallCenterMode.value) {
      isCallCenterMode.value = false
      pendingRetryAfterPhoneChange.value = true
      state.isDisplayGeneralErrorMessage = null
    }
  }
  const handleUseOtherEmail = () => {
    emit('useOtherEmail')
  }

  onMounted(async () => {
    await resetRegisterForm()

    const stop = onKeyStroke('Enter', () => {
      handleButtonClick()
    })
    onBeforeUnmount(() => stop())
  })

  watch(() => email.value, resetRegisterForm)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", null, [
    _cache[3] || (_cache[3] = _createElementVNode("div", { class: "d-flex flex-row nowrap justify-content-between heading" }, [
      _createElementVNode("span")
    ], -1)),
    _createVNode(InformationsForm, {
      form: _unref(registerForm),
      isEmailValid: _unref(isEmailValid),
      "show-errors": state.showErrors,
      "invalid-fields": _unref(invalidFields),
      onUpdateForm: _cache[0] || (_cache[0] = newForm => handleUpdateForm(newForm)),
      onUseOtherEmail: handleUseOtherEmail
    }, null, 8, ["form", "isEmailValid", "show-errors", "invalid-fields"]),
    _createVNode(ContactsForm, {
      form: _unref(registerForm),
      "show-errors": state.showErrors,
      "invalid-fields": _unref(invalidFields),
      onUpdateForm: _cache[1] || (_cache[1] = newForm => handleUpdateForm(newForm))
    }, null, 8, ["form", "show-errors", "invalid-fields"]),
    (showCdaOptIn.value)
      ? (_openBlock(), _createBlock(KeepInTouchOptIn, { key: 0 }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", null, [
        _createVNode(Button, {
          class: "px-2",
          disabled: btnDisabled.value,
          onButtonClick: handleButtonClick
        }, {
          text: _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(_unref(t)("loginRegister.validate")), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ])
    ]),
    (state.isDisplayGeneralErrorMessage)
      ? (_openBlock(), _createBlock(Banner, {
          key: 1,
          background: '#D4572A1A'
        }, {
          text: _withCtx(() => [
            _createElementVNode("span", _hoisted_2, _toDisplayString(state.isDisplayGeneralErrorMessage), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(ModalBase, {
      id: "register-call-center-modal",
      ref_key: "callCenterModalRef",
      ref: callCenterModalRef
    }, {
      footer: _withCtx(() => _cache[2] || (_cache[2] = [
        _createElementVNode("span", null, null, -1)
      ])),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("h3", null, _toDisplayString(_unref(t)('loginRegister.register_call_center_modal_title')), 1)
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("p", null, _toDisplayString(_unref(t)('loginRegister.register_call_center_modal_message')), 1),
            (callCenterContactInfo.value.openingHours)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)('loginRegister.register_call_center_modal_opening_hours')), 1),
                  _createElementVNode("span", null, _toDisplayString(callCenterContactInfo.value.openingHours), 1)
                ]))
              : _createCommentVNode("", true),
            (callCenterContactInfo.value.phone)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)('loginRegister.register_call_center_modal_phone')), 1),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(callCenterContactInfo.value.phone), 1),
                  _createElementVNode("a", {
                    class: "register-call-center-phone-mobile",
                    href: callCenterPhoneHref.value
                  }, _toDisplayString(callCenterContactInfo.value.phone), 9, _hoisted_11)
                ]))
              : _createCommentVNode("", true),
            (callCenterContactInfo.value.phone)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 2,
                  class: "register-call-center-call-button",
                  href: callCenterPhoneHref.value
                }, _toDisplayString(_unref(t)('loginRegister.register_call_center_modal_call_button')), 9, _hoisted_12))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _: 1
    }, 512)
  ]))
}
}

})